import BaseModel from '../BaseModel';
import Staff from '../Staff';
import MultiLanguageField from '../MultiLanguageField';
import * as AT from '../../../components/container/attributes';
import {HitUtils} from '@hit/components/src/utils/hit/HitUtils';
import {formatDate} from '../../../plugins/i18n/i18n';

export default class Form extends BaseModel {
  constructor({
    id,
    designation,
    author = undefined,
    finished = false,
    status = undefined,
    submissionDate = undefined,
    validationDate = undefined,
    injectionDate = undefined,
    injectionDataHitOffice = undefined,
    creationDate = undefined,
    formDate = undefined,
    formDefinitionDesignationFrBe = undefined,
    formDefinitionDesignationDeBe = undefined,
    formDefinitionDesignationNlBe = undefined,
    formDefinitionDesignationFrLu = undefined,
    formDefinitionDesignationDeLu = undefined,
    formDefinitionDesignationFrFr = undefined,
    formDefinitionDesignationDeDe = undefined,
    formDefinitionDesignationNlNl = undefined,
    formDefinitionDesignationEnGb = undefined,
    formDefinitionDesignationEnIn = undefined,
    formDefinitionDesignationMrIn = undefined,
    formDefinitionDesignationHiIn = undefined,
    linkedEntity = undefined,
    tags = [],
  }) {
    super(id, '', designation);
    this.author = author ? new Staff(author) : null;
    this.finished = finished;
    this.status = status;
    this.submissionDate = submissionDate;
    this.validationDate = validationDate;
    this.injectionDate = injectionDate;
    this.injectionDataHitOffice = injectionDataHitOffice;
    this.creationDate = creationDate ? new Date(creationDate) : null;
    this.formDate = formDate ? new Date(formDate) : null;
    this.formDefinitionDesignations = new MultiLanguageField({
      formDefinitionDesignationFrBe,
      formDefinitionDesignationDeBe,
      formDefinitionDesignationNlBe,
      formDefinitionDesignationFrLu,
      formDefinitionDesignationDeLu,
      formDefinitionDesignationFrFr,
      formDefinitionDesignationDeDe,
      formDefinitionDesignationNlNl,
      formDefinitionDesignationEnGb,
      formDefinitionDesignationEnIn,
      formDefinitionDesignationMrIn,
      formDefinitionDesignationHiIn,
    });
    this.formDefinitionDesignation = this.formDefinitionDesignations.value;
    this.linkedEntity = linkedEntity;
    this.tags = tags;
  }

  static get fkName() {
    return 'form_id';
  }
  static get apiRoute() {
    return 'form';
  }
  static get detailPageRoute() {
    return 'formItem';
  }
  static get listPageRoute() {
    return 'formItem';
  }
  get fkName() {
    return Form.fkName;
  }
  get apiRoute() {
    return Form.apiRoute;
  }
  get listPageRoute() {
    return Form.listPageRoute;
  }
  get detailPageRoute() {
    return Form.detailPageRoute;
  }
  static get orderKey() {
    return 'creation_date';
  }
  static get orderOrder() {
    return 'desc';
  }

  static get dataListConfig() {
    return {
      zone1Field: 'designation',
      zone2CustomFunction: (data) =>
        HitUtils.createStandardDesignation(
          {
            formDef: data.formDefinition?.designation,
            linkedEntity: data.linkedEntity,
          },
          'formDef',
          'linkedEntity',
          ' / ',
        ),
      zone3CustomFunction: (data) =>
        `${data.author?.designation}: ${formatDate(data.formDate, 'short')}`,
    };
  }
  get dataListConfig() {
    return Form.dataListConfig;
  }

  static get browserDisplayAttrs() {
    return this.getAttributes([
      'author',
      'formDefinition',
      'linkedEntity',
      'designation',
      'formDate',
    ]);
  }

  static get joinAttrsKey() {
    return ['designation', 'formDefinition', 'author', 'status'];
  }

  static get listDisplayAttrs() {
    return this.getAttributes([
      'author',
      'formDefinition',
      'linkedEntity',
      'designation',
      'tags',
      'formDate',
      'creationDate',
      'modificationDate',
    ]);
  }

  static extractLinkedEntityFromParent(data) {
    if (!data) return null;
    return HitUtils.createStandardDesignation(JSON.parse(data), 'no');
  }

  static get allAttributes() {
    return [
      this.DESIGNATION_ATTRIBUTE(),
      new AT.FkFormDefinitionAttribute({
        name: 'formDefinition',
        column: 'form_definition_id',
        joinPath: [
          'form_definition!fk_form_form_definition_id_form_definition!inner',
        ],
        tableProperties: {
          header: 'hit-base.common.form',
        },
      }),
      new AT.JsonObjectAttribute({
        name: 'linkedEntity',
        column: 'data->meta->parent->>data',
        tableProperties: {
          header: 'hit-base.form.linked-entity',
        },
        transform: (parentData) => {
          return this.extractLinkedEntityFromParent(parentData);
        },
      }),
      new AT.FkStaffAttribute({
        name: 'author',
        column: 'author_id',
        joinPath: ['staff!fk_form_author_id_staff'],
        tableProperties: {
          header: 'hit-base.common.author',
        },
        onlyFetchDesignation: true,
      }),
      new AT.BooleanAttribute({
        name: 'finished',
        tableProperties: {
          header: 'hit-app.common.finished',
        },
      }),
      new AT.StringAttribute({
        name: 'status',
        tableProperties: {
          header: 'hit-app.common.status',
        },
      }),
      new AT.DateAttribute({
        name: 'submissionDate',
        column: 'submission_date',
        tableProperties: {
          header: 'hit-app.form.submission-date',
        },
      }),
      new AT.DateAttribute({
        name: 'validationDate',
        column: 'validation_date',
        tableProperties: {
          header: 'hit-app.form.validation-date',
        },
      }),
      new AT.DateAttribute({
        name: 'injectionDate',
        column: 'injection_date',
        tableProperties: {
          header: 'hit-app.form.injection-date',
        },
      }),
      new AT.DateAttribute({
        name: 'injectionDataHitOffice',
        column: 'injection_data_hit_office',
        tableProperties: {
          header: 'hit-app.form.injection-data-hit-office',
        },
      }),
      new AT.DateAttribute({
        name: 'formDate',
        column: 'form_date',
        tableProperties: {
          header: 'hit-app.form.form-date',
        },
      }),
      new AT.DateAttribute({
        name: 'creationDate',
        column: 'creation_date',
        tableProperties: {
          header: 'hit-base.common.creation-date',
        },
      }),
      new AT.DateAttribute({
        name: 'modificationDate',
        column: 'modification_date',
        tableProperties: {
          header: 'hit-base.common.last-modified',
          hideByDefault: true,
        },
      }),
      this.TAGS_ATTRIBUTE({moduleUsage: 'form', tableOrigin: 'form'}),
    ];
  }

  static factory() {
    return new Form({});
  }

  static getMTORelations() {
    return {
      formDefinition: {
        childIdColumn: 'form_definition_id',
        childTableName: 'form_definition',
      },
      author: {
        childIdColumn: 'author_id',
        childTableName: 'staff',
      },
    };
  }
}
